import { allDroplets } from '@editor/droplets/all-droplets';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import browser from 'browser-detect';
import path from 'path';
import tinycolor from 'tinycolor2';
import moment from 'moment-timezone';
import droplets from '@editor/droplets/droplets.config.json';
import fonts from '@editor/fonts.json';
import { PAGE_TYPES } from '@/constants';

const packageJSON = require('$root/package.json');
const testPsuedos = new RegExp([':hover', ':focus'].join('|'), 'gi');

const drzUtils = {
  // using _ so these global method names won't clash with future local methods
  // parameter options are 'scope of component', $ref name
  computed: {
    ...mapGetters([
      's3DeleteQueue',
      'globalToolBarOpen',
      'globalBubbleOpen',
      'globalBubbleOptionsOpen',
      'showDroplets',
      'globalOpenModal',
      'editorLoader',
      'activeDroplet',
      'activeDropletItem',
      'globalDragEl',
    ]),
    _env() {
      return process.env.NODE_ENV;
    },
    _dev() {
      return process.env.NODE_ENV === 'development';
    },
    _prod() {
      return process.env.NODE_ENV === 'production';
    },
    _storm() {
      return process.env.ADMIN === 'storm';
    },
    _isChurch() {
      return this.$store.getters.account.accountType === 'church';
    },
    _isBundle() {
      return this.$store.getters.account.accountType === 'bundle';
    },
    _isExempt() {
      return this.$store.getters.account.accountType === 'exempt';
    },
    _siteExempt() {
      return this._currentSite && this._currentSite.accountType === 'exempt';
    },
    _published() {
      return this._currentSite && this._currentSite.lifecycle !== 'design';
    },
    _features() {
      const { features = {} } = this._currentSite;
      return features;
    },
    _discountHosting() {
      const enabled = this.currentSite &&
        this.currentSite.discount &&
        this.currentSite.discount.hosting &&
        this.currentSite.tier === 'Premium';
      return this._isBundle && enabled;
    },
    _siteOwner() {
      const { collaborators, owner = {} } = this._currentSite || {};
      const { _id } = this.currentUser;
      const isOwner = !collaborators ||
        (collaborators &&
        !collaborators[_id]);
      return isOwner || _id === owner._id;
    },
    _needsToMoveBack() {
      return this.globalOpenModal ||
        this.showDroplets ||
        this.globalBubbleOptionsOpen ||
        this.editorLoader.page ||
        this.globalToolBarOpen ||
        this.activeDroplet._id ||
        this.globalDragEl;
    },
    _useStaging() {
      return this.$store.getters.useStaging;
    },
    _cssPsuedoStates() {
      const states = [
        ':hover',
        ':focus',
        ':reveal',
      ];
      return new RegExp(states.join('|'), 'gi');
    },
    _defaultColorPickerObj() {
      return {
        a: 1,
        hex: '#FFFFFF',
        rgba: { a: 1, b: 255, g: 255, r: 255 },
        hsl: { a: 1, h: 0, l: 1, s: 0 },
        hsv: { a: 1, h: 0, s: 0, v: 1 },
        oldHue: 0,
        source: 'rgba',
      };
    },
    _paletteColors() {
      let list = [];
      const palette = this._currentSite.colorPalette;
      if (palette) {
        list = palette.map(color => this._concatPicker(color.value));
        // strip out duplicates
        const filtered = [];
        list.forEach((color) => {
          if (filtered.indexOf(color) < 0) {
            filtered.push(color);
          }
        });
        list = filtered;
      }
      return list;
    },
    _pageTypes() {
      return PAGE_TYPES;
    },
    _pageDiffIgnore() {
      return [
        'pageCSS',
        'description',
        'url',
        'name',
        'title',
        'meta',
        'defaultType',
        'defaultTitle',
        'defaultDescription',
        'description',
        'defaultMeta',
        'status',
      ];
    },
    _needsDragHelper() {
      return this.globalDragEl !== 'noDragHelper';
    },
    _timeZone() {
      return moment.tz.guess();
    },
  },
  data() {
    return {
      deb_wait: null,
      squashed: false,
      _mfaUser: {},
    };
  },
  methods: {
    async _reportError(params) {
      const version = packageJSON.version;
      const fullPath = _.clone(this.$route.fullPath);
      const message = {
        error: params.error.message || params.error,
      };
      const errorReport = {
        version,
        user: this.currentUser,
        route: { fullPath },
        agent: navigator.userAgent,
      };
      if (params.payload) {
        errorReport.payload = params.payload;
      }
      await this._trackEvent({
        type: 'error',
        meta: {
          message: JSON.stringify(message),
          origin_fn: params.fn,
          ...errorReport,
        },
      });
    },
    async _trackEvent(params) {
      if (this._env === 'production' && !this._storm) {
        const br = browser();
        params.browser = `${br.name} ${br.versionNumber}`;
        params.meta = params.meta || {};
        params.meta.os = br.os;
        params.meta.mobile = br.mobile;
        // NOTE: disabled to check if it affects dynamic costs
        await this._SEND([{
          name: 'trackEvent',
          payload: { event: params },
        }]);
        this.$log.debug('sent tracking event: ', params);
      }
    },
    _scrollToDroplet($el) {
      const speed = 250;
      setTimeout(() => {
        $('html, body').animate({
          scrollTop: $($el).offset().top - 150 }, speed);
      }, speed + 100);
    },
    // we need to debounce callbacks that are called on an event listener that runs an
    // excessive amount of times. ie: color picker change, resizing, input sliders etc.
    _debounce(fn, delay) {
      clearTimeout(this.deb_wait);
      this.deb_wait = setTimeout(() => {
        fn();
      }, delay);
    },
    async _runS3Queue(empty) {
      // this will check the s3 delete queue for droplets, if there is any, it will go
      // and delete those folders in s3
      const queue = _.cloneDeep(this.s3DeleteQueue);
      if (!empty && queue.length > 0) {
        await Promise.all(queue.map(async (droplet) => {
          // remove droplet files from s3
          const dropletPath = `_droplet-assets/${droplet.type}/${droplet._id}/`;
          const s3Path = `${this._sitePath}/${dropletPath}`;
          await this._s3emptyFolder({ path: `${this._customerFolder}/${s3Path}` });
          this.$log.debug(`empty assets in ${dropletPath}`);
          // delete thumbnails from s3 and static if needed
          if (droplets[droplet.type].storage.thumbnails) {
            const thumbPath = `_droplet-assets/_thumbnails/${droplet.type}/${droplet._id}/`;
            const s3ThumbPath = `${this._sitePath}/${thumbPath}`;
            await this._s3emptyFolder({ path: `${this._customerFolder}/${s3ThumbPath}` });
            this.$log.debug(`empty thumbnails in ${thumbPath}`);
          }
        }));
      }
      // clear queue in any case
      this.$store.dispatch('updateS3Queue', 'empty');
    },
    _deepOmit(input, props) {
      const omitDeepOnOwnProps = (obj) => {
        if (!_.isArray(obj) && !_.isObject(obj)) {
          return obj;
        }

        if (_.isArray(obj)) {
          return this._deepOmit(obj, props);
        }

        const o = {};
        _.forOwn(obj, (value, key) => {
          o[key] = this._deepOmit(value, props);
        });

        return _.omit(o, props);
      };

      if (arguments.length > 2) {
        props = Array.prototype.slice.call(arguments).slice(1); // eslint-disable-line
      }

      if (typeof input === 'undefined') {
        return {};
      }

      if (_.isArray(input)) {
        return input.map(omitDeepOnOwnProps);
      }

      return omitDeepOnOwnProps(input);
    },
    // we use this for any dragula container that allows droplets to be dropped in it
    _determineDroplet(drp) {
      const type = _.chain(drp.type)
        .split('.')
        .last()
        .camelCase()
        .upperFirst()
        .value();
      return allDroplets[type];
    },
    _formatLink(path) {
      // we use this util to ensure that all of our page references
      // begin and end with a "/" due to cloudfront/s3 limitations
      let url = path;
      const isHome = path === '/';
      if (!isHome && !url.endsWith('/')) {
        url = `${url}/`;
      }
      if (!isHome && !url.startsWith('/')) {
        url = `/${url}`;
      }
      return url;
    },
    _rule(drp, viewport, selector, property) {
      // helper we use to check if any css properties exist for a droplet
      const psuedos = this._cssPsuedoStates || testPsuedos;
      const inherits = ['transform', 'transition'];
      const sel = _.find(drp.mediaQueries, { selector });
      let pr;
      let status = 'not-present';
      if (sel) {
        const prop = _.find(sel.properties, { property });
        if (prop) {
          pr = prop;
        }
        if (prop && prop[viewport] !== 'default') {
          status = 'custom';
        }
        if (prop && prop[viewport] === 'default') {
          status = 'default';
        }
        // the only rules that should "inherit" from desktop are those that we can't get a default
        // style from the DOM (ie :hover, :focus, or matrix values from transitions)
        const mayInherit = status === 'default' && prop.desktop !== 'default' && viewport !== 'desktop';
        if (mayInherit ||
            ((mayInherit && selector.match(psuedos)) ||
            (mayInherit && inherits.includes(property)))) {
          status = 'inherit';
        }
      }
      if (status === 'not-present' && selector.match(psuedos)) {
        // this status means a value should be taken from the primary rule
        // since the psuedo version is not there yet.
        // primary meaning - a rule like .myclass:hover, .myclass would
        // be the primary
        status = 'psuedo-inherit';
      }
      return { status, prop: pr };
    },
    _getPickerVal($vm, selector, property, $el) {
      let val = this._defaultColorPickerObj;
      let vp = $vm.viewport;
      if (selector === '.drzNav-slideContainer' && (property === 'background-color' || property === 'color')) {
        vp = 'mobile';
      }
      const rule = this._rule($vm.droplet, vp, selector, property);
      if (rule.status === 'not-present' || rule.status === 'default') {
        val = this._getDefaultPickerVal(selector, property, $el);
      }
      if (rule.status === 'custom') {
        val = this._buildPickerVal(rule.prop[vp]);
      }
      if (rule.status === 'inherit') {
        val = this._buildPickerVal(rule.prop.desktop);
      }
      if (rule.status === 'psuedo-inherit') {
        const psuedos = this._cssPsuedoStates || testPsuedos;
        val = this._getPickerVal(
          $vm,
          selector.replace(psuedos, ''),
          property,
          $el);
      }
      return val;
    },
    _getDefaultPickerVal(selector, property, $el) {
      let defaultValue = this._defaultColorPickerObj;
      if ($el) {
        // so we can set the default color picker color when there
        // hasn't been one set yet. and property has a "default" val
        const styles = window.getComputedStyle($el);
        defaultValue = this._buildPickerVal(styles[property]);
      }
      return defaultValue;
    },
    _concatPicker(val) {
      return `rgba(${val.rgba.r}, ${val.rgba.g}, ${val.rgba.b}, ${val.rgba.a})`;
    },
    _buildPickerVal(val) {
      if (val === '' || val === 'none') {
        return this._defaultColorPickerObj;
      }
      let rgba = val.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d*(?:\.\d+)?))?\)/gi);
      rgba = rgba[0];
      // convert rgba string to number values readable by the color picker
      // example: "rgba(255, 255, 255, 0.34)"
      const vals = _.map(rgba.split(','), n => _.toNumber(n.replace(/[^0-9.]/g, '')));

      if (vals.length < 4) {
        // if there is not an alpha value, make it fully opaque
        vals.push(1);
      }
      const hex = _.toUpper(`#${tinycolor(rgba).toHex()}`);
      const hsv = tinycolor(hex).toHsv();
      hsv.a = vals[3];
      const hsl = tinycolor(hex).toHsl();
      hsl.a = vals[3];
      return {
        a: vals[3],
        hex,
        rgba: {
          a: vals[3],
          b: vals[2],
          g: vals[1],
          r: vals[0],
        },
        hsl,
        hsv,
        oldHue: 0,
        source: 'rgba',
      };
    },
    _getValTransform(params) {
      // transform format ex: rotateX(13px) rotateY(10px) rotate(33deg)
      let val = params.defaultVal || 0;
      let unit = 'px';
      if (params.type.match(/rotate|skew/gi)) {
        unit = 'deg';
      }
      if (params.type === 'scale') {
        unit = '';
      }
      const rule = this._rule(
        params.$vm.droplet,
        params.$vm.viewport,
        params.selector,
        params.property,
      );
      let vp = params.$vm.viewport;
      if (rule.status === 'inherit') {
        vp = 'desktop';
      }
      if (rule.status === 'custom' || rule.status === 'inherit') {
        params.$vm.currentVal = rule.prop[vp];
        // grab transform type from property and extract value
        const regex = new RegExp(`${params.type}\\(([^)]+)\\)`, 'gi');
        const exists = params.$vm.currentVal.match(regex);
        if (exists && exists.length === 1) {
          const get = this._extractVal(exists[0]);
          unit = get.unit;
          val = get.val;
        }
      }
      if (rule.status === 'psuedo-inherit') {
        const psuedos = this._cssPsuedoStates || testPsuedos;
        params.selector = params.selector.replace(psuedos, '');
        const extraction = this._getValTransform(params);
        val = extraction.val;
        unit = extraction.unit;
      }
      return { val, unit };
    },
    _getBgImg($vm, selector, property, drp) {
      let val = '';
      const rule = this._rule(drp, $vm.viewport, selector, property);
      let vp = $vm.viewport;
      if (rule.status === 'inherit' || rule.status === 'default') {
        vp = 'desktop';
      }
      const shouldInherit = rule.status === 'default' && $vm.viewport !== 'desktop' && rule.prop.desktop !== 'default';
      if (rule.status === 'custom' || rule.status === 'inherit' || shouldInherit) {
        val = rule.prop[vp];
        // alternative - /url\('|url\(/
        val = val.replace('url(\'', '');
        // alternative - /'\)|\)/
        val = val.replace('\')', '');
      }
      return val;
    },
    _buildTransformVal(p) {
      // this is called when updating a transition value. It checks if a transition
      // value exists or not then either adds a new value or replaces it if exists.
      // transform format ex: rotateX(13px) rotateY(10px) rotate(33deg)
      let v = '';
      // reset local currentVal
      p.$vm.currentVal = null;
      // if value exists from mediaQueries, we set a currentVal
      this._getValTransform({
        $vm: p.$vm,
        selector: p.selector,
        property: p.property,
        type: p.type,
      });
      if (p.$vm.currentVal) {
        const regex = new RegExp(`${p.type}\\(([^)]+)\\)`, 'gi');
        const exists = p.$vm.currentVal.match(regex);
        if (exists && exists.length === 1) {
          // replace old value from transform (translate or rotate)
          const num = parseFloat(p.el.value);
          let newValue = `${p.type}(${p.el.value}${p.unit})`;
          if (p.removeOnDefault && p.removeOnDefault.value === num) {
            newValue = '';
          }
          const empties = new RegExp(`${p.type}\\(\\)`, 'gi');
          v = p.$vm.currentVal.replace(empties, '');
          v = v.replace(regex, newValue);
        } else {
          // translate type not there yet so we concat it
          v = `${p.$vm.currentVal} ${p.type}(${p.el.value}${p.unit})`;
        }
      } else {
        // no value at all for transform so we add it for the first and only time
        v = `${p.type}(${p.el.value}${p.unit})`;
      }
      if (v.trim() === '') {
        v = 'default';
      }
      return v;
    },
    _getVal(params) {
      // to get px related values
      let val = params.defaultVal || 0;
      let unit = params.defaultUnit || 'px';
      const rule = this._rule(
        params.$vm.droplet,
        params.$vm.viewport,
        params.selector,
        params.property,
      );
      if ((rule.status === 'not-present' || rule.status === 'default') && params.$el) {
        val = this._getDomStyle(params.property, params.$el);
      }
      if (rule.status === 'custom') {
        val = rule.prop[params.$vm.viewport];
      }
      if (rule.status === 'inherit' || (rule.status === 'default' && _.isNull(params.$el))) {
        val = rule.prop.desktop;
      }
      if (rule.status === 'psuedo-inherit') {
        const psuedos = this._cssPsuedoStates || testPsuedos;
        params.selector = params.selector.replace(psuedos, '');
        const get = this._getVal(params);
        val = get.val;
        unit = get.unit;
      }
      if (_.isString(val)) {
        const extraction = this._extractVal(val);
        val = extraction.val;
        unit = extraction.unit || unit;
      }
      return { val, unit };
    },
    _getDomStyle(property, $el) {
      let val = 0;
      const styles = window.getComputedStyle($el);
      const style = styles[property];
      val = this._extractVal(style).val;
      return val;
    },
    _getPropertyRule(params) {
      const vp = this.viewport;
      let val = params.default;
      const rule = this._rule(
        params.$vm.activeDroplet,
        vp,
        params.selector,
        params.property,
      );
      if ((rule.status === 'not-present' && params.$el) || rule.status === 'default') {
        // in this case, the user is trying to grab from certain item
        // so we need to fish for values inside item group styles
        if (params.groupParent) {
          params.selector = params.groupParent.selector.replace(/\$\{state\}/gi, params.$vm.activeState);
          params.groupParent = false;
          val = this._getPropertyRule(params);
        } else {
          const styles = window.getComputedStyle(params.$el);
          val = styles[params.property];
        }
      }
      if (rule.status === 'custom') {
        val = rule.prop[vp];
      }
      if (rule.status === 'inherit') {
        val = rule.prop.desktop;
      }
      if (rule.status === 'psuedo-inherit') {
        const psuedos = this._cssPsuedoStates || testPsuedos;
        params.selector = params.selector.replace(psuedos, '');
        val = this._getPropertyRule(params);
      }
      return val;
    },
    _extractVal(v) {
      // for creating the value from css strings
      const nonUnits = new RegExp(/[^a-z|%]/, 'i');
      // this should match decimals, negative and positive numbers
      const num = new RegExp(/-?(\d+\.?\d*|\d*\.?\d+)/g);
      const numVal = v.match(num) ? v.match(num)[0] : 0;
      const unit = v.split(num).reverse()[0].replace(nonUnits, '');
      return {
        val: _.toNumber(numVal),
        unit: unit !== 'normal' ? unit : 'px',
      };
    },
    _extractTransitionValue(val, rule) {
      let s = val.split(',');
      s = s.map(r => r.trim());
      const string = new RegExp(`^${rule} \\d+(\\.\\d{1,2})?s`, 'gi');
      s = s.find(r => r.match(string));
      return s ? _.toNumber(s.replace(/[^0-9.]/g, '')) : 0;
    },
    _getValTransition(params) {
      let val = 0;
      const config = params.styling.components[params.$vm.activeComponent];
      if (params.$vm.activeComponent && config) {
        const el = config.selectors[0];
        const rule = this._rule(
          params.$vm.droplet,
          params.$vm.viewport,
          el.selector,
          'transition');
        let vp = params.$vm.viewport;
        if (rule.status === 'inherit') {
          vp = 'desktop';
        }
        if (rule.status === 'custom' || rule.status === 'inherit') {
          val = this._extractTransitionValue(rule.prop[vp], el.properties[0].property);
        }
      }
      return val;
    },
    _buildTransitionVal(val, rule, trval) {
      // transition format ex: background-color 0.2s, color 1s, padding 0.5s
      let list = val.split(',');
      list = list.map(r => r.trim());
      // TODO here is where we'd need to change s/ms
      const string = new RegExp(`^${rule} \\d+(\\.\\d{1,2})?s`, 'gi');
      const ruleIndex = list.findIndex(r => r.match(string));
      if (ruleIndex !== -1) {
        // rule exists so replace it in array
        list[ruleIndex] = `${rule} ${trval}s`;
      } else {
        // rule is not there, concat it
        list.push(`${rule} ${trval}s`);
      }
      return list.join(', ');
    },
    _updateTransitions($vm, styling, scope) {
      const config = styling.components[$vm.activeComponent];
      if ($vm.activeState === ':hover' && config) {
        this._debounce(() => {
          _.forEach(config.selectors, (item) => {
            const rule = this._rule($vm.droplet, $vm.viewport, item.selector, 'transition');
            const hovers = _.filter(item.properties, p => p.hover);
            let val = '';
            if (rule.status === 'custom') {
              // if rule already exists for selector, find the property and replace it
              let currentVal = rule.prop[$vm.viewport];
              _.forEach(hovers, (p) => {
                val = this._buildTransitionVal(currentVal, p.property, $vm.display.transition);
                currentVal = val;
              });
            }
            if (rule.status === 'not-present' || rule.prop[$vm.viewport] === 'default') {
              const values = _.map(hovers, h => `${h.property} ${$vm.display.transition}s`);
              val = values.join(', ');
            }
            // we set the transition on the entire selector and not :hover
            // for smoothe transitions all the way around
            const payload = {
              drp: $vm.droplet,
              selector: item.selector,
              property: 'transition',
              val,
              viewport: $vm.viewport,
            };
            this.$store.dispatch('changeDropletCss', payload);
            if (scope && scope.callCssGenerator) {
              scope.callCssGenerator();
            } else {
              $vm.$emit('css-generator');
            }
          });
        }, 250);
      }
    },
    _buildShadowVal(params) {
      const coords = {
        horizontal: `${params.coords.horizontal}${params.coords.horizontal_unit}`,
        vertical: `${params.coords.vertical}${params.coords.vertical_unit}`,
        blur: `${params.coords.blur}${params.coords.blur_unit}`,
        spread: `${params.coords.spread}${params.coords.spread_unit}`,
        type: `${params.coords.type}`,
      };
      if (params.options.hiddenInputs) {
        _.forOwn(coords, (value, key) => {
          if (params.options.hiddenInputs.includes(key)) {
            coords[key] = '';
          }
        });
      }
      return `${coords.type} ${coords.horizontal} ${coords.vertical} ${coords.blur} ${coords.spread} ${this._concatPicker(params.color)}`;
    },
    _getValShadow(params) {
      let shadow = false;
      const rule = this._rule(
        params.$vm.droplet,
        params.$vm.viewport,
        params.selector,
        params.property,
      );
      if ((rule.status === 'not-present' || rule.status === 'default') && params.$el) {
        const styles = window.getComputedStyle(params.$el);
        shadow = styles[params.type];
      }
      if (rule.status === 'inherit') {
        shadow = rule.prop.desktop;
      }
      if (rule.status === 'custom') {
        shadow = rule.prop[this.viewport];
      }
      if (rule.status === 'psuedo-inherit') {
        const psuedos = this._cssPsuedoStates || testPsuedos;
        params.selector = params.selector.replace(psuedos, '');
        shadow = this._getValShadow(params);
      }
      const set = {};
      const data = {
        horizontal: '2px',
        vertical: '3px',
        blur: '10px',
        spread: '0px',
        type: '',
        color: 'rgba(0, 0, 0, 0.15)',
      };

      // this would mean the shadow object has been constructed already.
      // usually when a recursive fn finds this as a psuedo inherit rule
      if (_.isPlainObject(shadow)) {
        return shadow;
      }

      if (shadow && shadow !== 'none' && !_.isPlainObject(shadow)) {
        const color = shadow.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)/);
        const type = shadow.match(/inset/gi);
        data.type = type ? type[0] : '';
        data.color = color[0];
        _.forEach(shadow.split(' '), (v) => {
          if (v.match(/\d{1,3}px/gi)) {
            // h-offset v-offset blur spread
            if (!set.horizontal) {
              data.horizontal = v;
              set.horizontal = true;
            } else if (!set.vertical) {
              data.vertical = v;
              set.vertical = true;
            } else if (!set.blur) {
              data.blur = v;
              set.blur = true;
            } else if (!set.spread) {
              data.spread = v;
              set.spread = true;
            }
          }
        });
      }
      data.horizontal = this._extractVal(data.horizontal);
      data.vertical = this._extractVal(data.vertical);
      data.blur = this._extractVal(data.blur);
      data.spread = this._extractVal(data.spread);
      return data;
    },
    _getFontFamily($vm, selector, property, $el) {
      let font = fonts.defaultFont;
      const rule = this._rule($vm.droplet, $vm.viewport, selector, property);
      if ((rule.status === 'not-present' || rule.status === 'default') && $el) {
        const styles = window.getComputedStyle($el);
        font = styles.fontFamily.split(',')[0].replace(/['"]+/g, '');
      }
      if (rule.status === 'inherit') {
        font = rule.prop.desktop;
      }
      if (rule.status === 'custom') {
        font = rule.prop[this.viewport];
      }
      if (rule.status === 'psuedo-inherit') {
        const psuedos = this._cssPsuedoStates || testPsuedos;
        font = this._getFontFamily(
          $vm,
          selector.replace(psuedos, ''),
          property,
          $el);
      }
      return font;
    },
    _buildStateSelector(params) {
      let final = '';
      const selectors = params.selector.split(',');
      const last = selectors.length - 1;
      _.forEach(selectors, (s, i) => {
        let next = ', ';
        if (i === last) {
          next = '';
        }
        final += `${s}${params.activeState}${next}`;
      });
      return final;
    },
    _isBoilerplateAsset(item) {
      let bg = '';
      if (this._currentSite && this._currentSite.boilerplate) {
        bg = this._currentSite.boilerplate.name.toLowerCase();
        bg = bg.replace(/[^a-zA-Z0-9]/g, '-');
        bg = `${bg}/`;
      }
      const bpAsset = new RegExp(`/boilerplates/${bg}`, 'gi');
      return item.match(bpAsset);
    },
    _dropletType(droplet) {
      const hasType = droplet.type;
      const split = hasType ? hasType.split('.') : false;
      const topLevel = split ? split.length === 2 : false;
      const noDot = split ? split.length === 1 : false;
      const type = topLevel ? split[0] : false;
      const name = topLevel ? split[1] : '';
      const typeOne = noDot ? split[0] : false;
      const label = typeof name === 'string' && name !== '' ? droplets[name].label : false;
      return {
        name: typeOne || name,
        label,
        normal: type === 'droplet',
        puddle: type === 'puddle',
        section: typeOne === 'sectionBlock',
        any: type === 'droplet' || type === 'puddle' || typeOne === 'sectionBlock',
      };
    },
    _getFromThumb(file) {
      // this takes thumbnial file names and checks for a custom
      // suffix (ie: __png__) and grabs the corresponding file using
      // that file type

      // essentially this should turn a thumbnail file name like this:
      // filename_here__jpeg__.png to filename_here.jpeg
      const ext = path.extname(file);
      let name = file;
      const override = new RegExp(/(__[^)]{0,5}__\.)/, 'gi');
      let overrideExt = file.match(override);
      if (overrideExt && overrideExt[0]) {
        name = `${path.basename(file, ext)}.`;
        overrideExt = overrideExt[0].replace(/[^a-z0-9]/gi, '');
        name = `${name.replace(override, '')}.${overrideExt}`;
      }
      return name;
    },
    _getThumbFromFile(file) {
      let name = file;
      if (file.startsWith('/')) {
        const ext = path.extname(file);
        const suffix = path.extname(file).replace(/\./, '');
        const fileName = path.basename(file, ext);
        name = `${fileName}__${suffix}__.png`;
      }
      return name;
    },
    _zoneFormat({ date, format, zone }) {
      return moment(date).tz(zone || this._timeZone).format(format || 'MM/DD/YYYY');
    },
    _getKeyVal(obj, keys) {
      let value = obj;
      const nest = keys.split('.');
      nest.forEach((item) => {
        if (value) {
          value = value[item];
        }
      });
      return value;
    },
    _canDeletePage(item) {
      // don't allow a page to be deleted if it has children in the sitemap
      let noChildren = true;
      if (item.type === 'page' && !item.isHomepage) {
        const page = this._goFind({
          entry: this._currentSite.siteMap,
          value: item._id,
        }).found;
        noChildren = (page && page.subPages.length < 1) || !page;
      }
      // if a template is in use, don't allow it to be deleted
      let tplNotInUse = true;
      if (item.type === 'template') {
        const pages = _.filter(this._currentSite.pages, page => page.templateID === item._id);
        tplNotInUse = pages.length < 1;
      }
      return (
        (!item.isHomepage && item.type !== 'template' && noChildren) ||
        (item.isHomepage && item.type === 'draft') ||
        (item.type === 'template' && tplNotInUse)
      );
    },
    _blur(params) {
      return this.activeDroplet._id &&
             this.activeDroplet._id === params.droplet._id &&
             this.activeDropletItem._id &&
             this.activeDropletItem._id !== params.item._id;
    },
    _getInputVal(val) {
      return val.trim() !== '' ? val : false;
    },
    _getNumberVal({ val, fallback }) {
      const hasFallback = typeof fallback !== 'undefined' ? fallback : 1;
      return val !== '' ? parseInt(val, 10) : hasFallback;
    },
    // this is used for droplets that need assets uploaded to static and s3. Most have mock asset's
    // when first created, located in our cdn so when that is the case, we don't want to prefix
    // the static folder, we want to allow the full url
    // Note that some jquery plugins (map and section) contain a similar function inside of them
    // that does this since the src of those assets are initiated inside the plugin. What this means
    // is if you need to make a change there, you need to there also
    _servePrefix(src) {
      return src.match(/^http/gi) ? '' : `${process.env.S3_WEBSITE_BUCKET_URL}/${this._fullSiteFolder}`;
    },
    _isCollaborator({ site }) {
      const { collaborators = {} } = site;
      return collaborators[this.currentUser._id];
    },
    _currentDynamic(type) {
      const dynamic = this._currentSite.dynamic && this._currentSite.dynamic[type];
      return dynamic || {};
    },
    _getNumber(val) {
      return typeof val === 'string' ? parseInt(val, 10) : val;
    },
    _startCase(string) {
      return string.replace(/\w+/g, _.startCase);
    },
  },
};

export { drzUtils }; // eslint-disable-line



// WEBPACK FOOTER //
// ./src/mixins/utils.js